import { useState } from 'react';

import { Building2, Check, ChevronLeft, ChevronRight, Copy, Globe, ListFilter, Pencil, Search } from 'lucide-react';
import { useDebouncedValue } from 'shared/hooks/useDebouncedValue';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import {
  GlobalPromptSection,
  ProjectPromptSection,
  useGetGlobalPromptSectionsQuery,
  useGetProjectPromptSectionsQuery,
} from '../api/promptSections.api';
import type { PromptSectionPermission } from '../api/promptSections.api';
import { PROMPT_SECTION_CATEGORY_META, type PromptSectionCategory } from '../lib/promptSectionCategories';
import PromptSectionCategoriesPicker from './PromptSectionCategoriesPicker';
import PromptSectionEditModal from './PromptSectionEditModal';
import type { EditableSection } from './PromptSectionEditModal';

const ITEMS_PER_PAGE = 8;

interface SectionLike {
  id: number;
  name: string;
  displayName: string;
  value: string;
  permission: PromptSectionPermission;
  promptTag?: string;
  categories?: PromptSectionCategory[];
}

interface SectionRowProps {
  section: SectionLike;
  copiedSection: string | null;
  onCopy: (sectionName: string, promptTag?: string) => void;
  onEdit: (section: SectionLike) => void;
}

const SectionRow = ({ section, copiedSection, onCopy, onEdit }: SectionRowProps) => {
  const isCopied = copiedSection === section.name;
  return (
    <li className="rounded-md border border-border-subtle bg-bg-surface px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-text-default">{section.displayName}</p>
          {section.categories && section.categories.length > 0 && (
            <div className="my-1 flex max-w-64 flex-wrap gap-1">
              {section.categories.map((category) => (
                <Badge
                  key={category}
                  variant="outline"
                  className={cn('h-4 px-1.5 text-[10px]', PROMPT_SECTION_CATEGORY_META[category].chip)}
                >
                  {PROMPT_SECTION_CATEGORY_META[category].text}
                </Badge>
              ))}
            </div>
          )}
          <p className="truncate text-xs text-text-muted">{section.promptTag ?? section.name}</p>
        </div>
        <div className="flex shrink-0 items-center">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => onCopy(section.name, section.promptTag)}
            aria-label="Copiar etiqueta"
          >
            {isCopied ? <Check className="size-3.5 text-brand-turquoise" /> : <Copy className="size-3.5" />}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => onEdit(section)}
            aria-label="Editar sección"
          >
            <Pencil className="size-3.5" />
          </Button>
        </div>
      </div>
      {section.value && (
        <p className="mt-1 line-clamp-3 whitespace-pre-line text-xs text-text-muted">{section.value}</p>
      )}
    </li>
  );
};

interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({ page, totalPages, onPageChange }: PaginationProps) => {
  if (totalPages <= 1) return null;
  return (
    <div className="mt-3 flex items-center justify-between">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        aria-label="Página anterior"
      >
        <ChevronLeft className="size-3.5" />
      </Button>
      <span className="text-xs text-text-muted">
        {page}
        {' / '}
        {totalPages}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
        aria-label="Página siguiente"
      >
        <ChevronRight className="size-3.5" />
      </Button>
    </div>
  );
};

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const SearchInput = ({ value, onChange, placeholder = 'Buscar secciones' }: SearchInputProps) => (
  <div className="relative">
    <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-text-muted" />
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="pl-7 text-xs"
    />
  </div>
);

interface Props {
  projectId: number;
}

const PromptSectionsPanel = ({ projectId }: Props) => {
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const [openAccordion, setOpenAccordion] = useState<string>('global');

  const [globalSearch, setGlobalSearch] = useState('');
  const [globalCategories, setGlobalCategories] = useState<PromptSectionCategory[]>([]);
  const [globalPage, setGlobalPage] = useState(1);

  const [projectSearch, setProjectSearch] = useState('');
  const [projectPage, setProjectPage] = useState(1);

  const [editing, setEditing] = useState<{ section: EditableSection; isGlobal: boolean } | null>(null);

  const globalSearchDebounced = useDebouncedValue(globalSearch.trim(), 350);
  const projectSearchDebounced = useDebouncedValue(projectSearch.trim(), 350);

  const handleCopy = (sectionName: string, promptTag?: string) => {
    const text = promptTag ?? sectionName;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedSection(sectionName);
        setTimeout(() => setCopiedSection(null), 2000);
      })
      .catch(() => {
        // noop, clipboard might be unavailable
      });
  };

  const {
    data: globalData,
    isLoading: globalLoading,
    error: globalError,
  } = useGetGlobalPromptSectionsQuery({
    offset: (globalPage - 1) * ITEMS_PER_PAGE,
    limit: ITEMS_PER_PAGE,
    searchTerm: globalSearchDebounced || undefined,
    categories: globalCategories.length > 0 ? globalCategories : undefined,
  });
  const globalSections: GlobalPromptSection[] = globalData?.globalPromptSections ?? [];
  const globalTotal = globalData?.total ?? 0;
  const globalTotalPages = globalData?.totalPages ?? 1;

  const {
    data: projectData,
    isLoading: projectLoading,
    error: projectError,
  } = useGetProjectPromptSectionsQuery({
    projectId,
    offset: (projectPage - 1) * ITEMS_PER_PAGE,
    limit: ITEMS_PER_PAGE,
    searchTerm: projectSearchDebounced || undefined,
  });
  const projectSections: ProjectPromptSection[] = projectData?.promptSections ?? [];
  const projectTotal = projectData?.total ?? 0;
  const projectTotalPages = projectData?.totalPages ?? 1;

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border-subtle px-4 pb-3 pt-2">
        <p className="text-base font-semibold text-text-default">Panel de Gestión</p>
        <p className="text-xs text-text-muted">Secciones globales y del proyecto</p>
        <p className="mt-1 text-[11px] italic text-text-muted">
          Los permisos solo afectan el acceso/lectura para usuarios no admin. No afectan la generación del prompt.
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4">
        <Accordion
          type="single"
          collapsible
          value={openAccordion}
          onValueChange={(value) => setOpenAccordion(value)}
          className="w-full"
        >
          <AccordionItem value="global" className="border-b border-border-subtle">
            <AccordionTrigger className="px-1 py-3">
              <div className="flex items-center gap-2">
                <Globe className="size-4 text-brand-turquoise" />
                <span className="text-sm font-semibold text-text-default">Secciones Globales</span>
                <Badge variant="secondary" className="text-[10px]">
                  {globalTotal}
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-3">
              <div className="flex flex-col gap-3 pt-1">
                <SearchInput
                  value={globalSearch}
                  onChange={(value) => {
                    setGlobalSearch(value);
                    setGlobalPage(1);
                  }}
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" size="sm" className="justify-start gap-2">
                      <ListFilter className="size-3.5" />
                      {globalCategories.length > 0
                        ? `${globalCategories.length} categoría${globalCategories.length > 1 ? 's' : ''}`
                        : 'Filtrar por categoría'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-80">
                    <PromptSectionCategoriesPicker
                      value={globalCategories}
                      onChange={(categories) => {
                        setGlobalCategories(categories);
                        setGlobalPage(1);
                      }}
                    />
                    {globalCategories.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-3 w-full"
                        onClick={() => {
                          setGlobalCategories([]);
                          setGlobalPage(1);
                        }}
                      >
                        Limpiar filtro
                      </Button>
                    )}
                  </PopoverContent>
                </Popover>
                {globalLoading && (
                  <div className="flex flex-col gap-2">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                )}
                {globalError && (
                  <Alert variant="destructive">
                    <AlertDescription>Error al cargar las secciones globales.</AlertDescription>
                  </Alert>
                )}
                {!globalLoading && !globalError && globalSections.length === 0 && (
                  <p className="rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-xs text-text-muted">
                    {globalSearch
                      ? 'No se encontraron secciones con ese criterio'
                      : 'No hay secciones globales disponibles'}
                  </p>
                )}
                {!globalLoading && globalSections.length > 0 && (
                  <ul className="flex flex-col gap-2">
                    {globalSections.map((section) => (
                      <SectionRow
                        key={section.id}
                        section={section}
                        copiedSection={copiedSection}
                        onCopy={handleCopy}
                        onEdit={(s) => setEditing({ section: s, isGlobal: true })}
                      />
                    ))}
                  </ul>
                )}
                <Pagination page={globalPage} totalPages={globalTotalPages} onPageChange={setGlobalPage} />
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="project" className="border-none">
            <AccordionTrigger className="px-1 py-3">
              <div className="flex items-center gap-2">
                <Building2 className="size-4 text-brand-vivid" />
                <span className="text-sm font-semibold text-text-default">Secciones del Proyecto</span>
                <Badge variant="outline" className="text-[10px]">
                  {projectTotal}
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-3">
              <div className="flex flex-col gap-3 pt-1">
                <SearchInput
                  value={projectSearch}
                  onChange={(value) => {
                    setProjectSearch(value);
                    setProjectPage(1);
                  }}
                />
                {projectLoading && (
                  <div className="flex flex-col gap-2">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                )}
                {projectError && (
                  <Alert variant="destructive">
                    <AlertDescription>Error al cargar las secciones del proyecto.</AlertDescription>
                  </Alert>
                )}
                {!projectLoading && !projectError && projectSections.length === 0 && (
                  <p className="rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-xs text-text-muted">
                    {projectSearch
                      ? 'No se encontraron secciones con ese criterio'
                      : 'No hay secciones específicas para este proyecto'}
                  </p>
                )}
                {!projectLoading && projectSections.length > 0 && (
                  <ul className="flex flex-col gap-2">
                    {projectSections.map((section) => (
                      <SectionRow
                        key={section.id}
                        section={section}
                        copiedSection={copiedSection}
                        onCopy={handleCopy}
                        onEdit={(s) => setEditing({ section: s, isGlobal: false })}
                      />
                    ))}
                  </ul>
                )}
                <Pagination page={projectPage} totalPages={projectTotalPages} onPageChange={setProjectPage} />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {editing && (
        <PromptSectionEditModal
          section={editing.section}
          isGlobal={editing.isGlobal}
          projectId={projectId}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

export default PromptSectionsPanel;
